import {
  QueryFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback } from 'react';

import { ErrorResponse } from '../api/hscan';
import {
  requestHospitals,
  requestPayments,
  requestPaymentMailingOrder,
  requestStudiesFromHospital,
  Study,
  pushStudy,
  verifyPayment,
  requestRequestedImageHistory,
  requestStoredImageHistory,
  requestHospital,
  RequestHospitalParams,
  Hospital,
  PaymentBody,
  requestPay,
  MailingOrder,
  RequestIssuancesInPaymentParam,
  requestIssuancesInPayment,
  RequestStoreBundleParams,
  requestStoreBundle,
  requestStoreFormsInBundle,
  RequestStoreFormsInBundleParams,
  DicomImagePayment,
  PriceModel,
  PushStudyParams,
  PushStudyBody,
  RequestSizeOfStudyParams,
  requestFileSizeOfStudy,
  requestLocalIssuanceInPayment,
} from '../api/hscan/hospital';

import { useUserState } from './accountHook';
import { MutationOption, QueryOption } from './react-query-type';

export const useHospitals = (
  query?: string,
  options?: QueryOption<Hospital[]>,
) => {
  const fetch = async () => {
    const { data } = await requestHospitals({ query });
    return data;
  };

  return useQuery({
    queryKey: ['HOSPITALS', query],
    queryFn: fetch,
    ...options,
  });
};

export const useHospital = (
  params: RequestHospitalParams,
  options?: QueryOption<Hospital>,
) => {
  const fetch: QueryFunction<Hospital> = async ({ signal }) => {
    const { data } = await requestHospital(params, signal);
    return data;
  };

  return useQuery({
    queryKey: ['HOSPITAL', params],
    queryFn: fetch,
    ...options,
  });
};

export const useStudiesFromHospital = (
  form: {
    id: string;
    registration?: string;
  },
  options?: QueryOption<Study[], Study[], AxiosError<ErrorResponse>>,
) => {
  const [user] = useUserState();

  const fetch = async () => {
    const { data } = await requestStudiesFromHospital(form);
    return data;
  };

  return useQuery({
    queryKey: ['STUDIES_FROM_HOSPITAL', form.id, user?.userId],
    queryFn: fetch,
    ...options,
  });
};

export const useFileSizeOfStudy = (
  params: RequestSizeOfStudyParams,
  options?: QueryOption<number, number, AxiosError<ErrorResponse>>,
) => {
  const [user] = useUserState();

  const fetch = async () => {
    const { data } = await requestFileSizeOfStudy(params);
    return data;
  };

  return useQuery({
    queryKey: ['FILE_SIZE_OF_STUDY', params.studyInstanceUID, user?.userId],
    queryFn: fetch,
    ...options,
  });
};

export const usePaymentList = (page = 0, size = 5) => {
  const fetch = async () => {
    return requestPayments({ page, size });
  };

  return useQuery({
    queryKey: ['PAYMENT', page, size],
    queryFn: fetch,
  });
};

export const useIssuancesInPayment = (
  params: RequestIssuancesInPaymentParam,
) => {
  const fetch = async () => {
    const { data } = await requestIssuancesInPayment(params);
    return data;
  };

  return useQuery({
    queryKey: ['ISSUANCES_IN_PAYMENT', params],
    queryFn: fetch,
  });
};

export const useLocalIssuanceInPayment = (
  params: RequestIssuancesInPaymentParam,
) => {
  const fetch = async () => {
    const { data } = await requestLocalIssuanceInPayment(params);
    return data;
  };

  return useQuery({
    queryKey: ['LOCAL_ISSUANCES_IN_PAYMENT', params],
    queryFn: fetch,
  });
};

export const useMailingOrder = (
  id: string,
  options?: QueryOption<MailingOrder>,
) => {
  const fetch = async () => {
    const { data } = await requestPaymentMailingOrder({ paymentId: id });
    return data;
  };

  return useQuery({
    queryKey: ['PAYMENT', 'ORDER', id],
    queryFn: fetch,
    enabled: !!id.length,
    ...options,
  });
};

export const usePushStudy = (
  options?: MutationOption<
    PushStudyParams & PushStudyBody,
    void,
    AxiosError<ErrorResponse>
  >,
) => {
  const mutate = useCallback(
    async ({ caseId, id, registration }: PushStudyParams & PushStudyBody) => {
      pushStudy({ id, registration }, { caseId });
    },
    [],
  );

  return useMutation({ mutationFn: mutate, ...options });
};

const DEFAULT_STUDY_PAGE_SIZE = 10;

export const useRequestedImageHistory = (id: string) => {
  const fetch = useCallback(async () => {
    const { data } = await requestRequestedImageHistory({ id });
    return data;
  }, [id]);

  return useQuery({
    queryKey: ['REQUESTED_IMAGE_HISTORY', id],
    queryFn: fetch,
    enabled: !!id.length,
  });
};

export interface StudiesPrice {
  price: number;
  taxFree: number;
  priceModel?: PriceModel;
  numStudies: number;
  hospitalName: string;
  isPriceLoading?: boolean;
  isPriceError?: boolean;
  size?: number;
}

export const useVerifyPayment = (options?: MutationOption<string, void>) => {
  const queryClient = useQueryClient();
  const mutate = async (id: string) => {
    verifyPayment({ id });
  };

  return useMutation({
    mutationFn: mutate,
    onSuccess: (...params) => {
      options?.onSuccess?.(...params);
      queryClient.invalidateQueries({ queryKey: ['requestCase'] });
    },
    ...options,
  });
};

export const useStoredImageHistory = (id: string) => {
  const fetch = useCallback(async () => {
    const { data } = await requestStoredImageHistory({ id });
    return data;
  }, [id]);

  return useQuery({
    queryKey: ['STORED_STUDY_HISTORY', id],
    queryFn: fetch,
    enabled: !!id.length,
  });
};

export const useStoreBundleList = (
  params: RequestStoreBundleParams = { size: DEFAULT_STUDY_PAGE_SIZE },
) => {
  const fetch = useCallback(async () => {
    const { data } = await requestStoreBundle(params);
    return data;
  }, [params]);
  return useQuery({
    queryKey: ['STORE_REQUEST_BUNDLE_LIST', params],
    queryFn: fetch,
  });
};

export const useStoreFromsInBundle = (
  params: RequestStoreFormsInBundleParams,
) => {
  const fetch = useCallback(async () => {
    const { data } = await requestStoreFormsInBundle(params);
    return data;
  }, [params]);
  return useQuery({
    queryKey: ['STORE_REQUEST_BY_ID', params],
    queryFn: fetch,
  });
};

export const useRequestPay = (
  options?: MutationOption<
    { body: PaymentBody; registration?: string },
    DicomImagePayment,
    AxiosError<ErrorResponse>
  >,
) => {
  const fetch = useCallback(
    async (form: { body: PaymentBody; registration?: string }) => {
      const { data } = await requestPay(form.body, form.registration);
      return data;
    },
    [],
  );

  return useMutation({ mutationFn: fetch, ...options });
};
